import clsx from 'clsx'
import { replace } from 'connected-react-router'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import Pane from 'components/Common/Pane'
import OfferPageGallery from '../OfferPageGallery'

import { setRecentlyUsedAirportCode } from 'actions/AuthActions'
import { clearBookingCalendar, fetchCalendars, fetchCalendarsByOccupancy } from 'actions/CalendarActions'
import {
  fetchAvailableRatesForEachRoom,
  fetchAvailableRatesForOffer,
  fetchOfferFlightPrice,
  fetchRelatedTravelItems,
} from 'actions/OfferActions'
import { CLEAR_OFFER_CHECK_IN_CHECK_OUT, SET_OFFER_CHECK_IN_CHECK_OUT } from 'actions/actionConstants'

import Breakpoint from 'components/Common/Breakpoint'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import DesktopRelatedTravelItems from 'components/OfferPage/RelatedTravelItems'
import ExpiredDealsLereRecommendations from 'components/Recommendations/ExpiredDeals/ExpiredDealsLereRecommendations'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import { urlHashDatePicker } from 'constants/url'
import type { OfferPageFilterType, OfferPageMultiRoomMode, OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useMountedEffect from 'hooks/useMountedEffect'
import usePrevious from 'hooks/usePrevious'
import { nonNullable } from 'lib/array/arrayUtils'
import { isValidOccupancy, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { hasOfferExpired } from 'lib/offer/offerUtils'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import type { OfferDurationOption } from '../Filters/DurationTabs/OfferDurationTabs'
import OfferIntro from './FlashHotelIntro'
import FlashHotelSidebarFilters from './FlashHotelSidebarFilters'
import FlashHotelHeader from './FlashHotelStickyHeader'
import HotelOfferPageStickyFooter from '../HotelOfferPageStickyFooter'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import LuxPlusNotificationBanner from 'luxPlus/components/Banners/LuxPlusNotificationBanner'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import AccommodationOfferPageStickyFooter from 'components/OfferPage/Accommodation/AccommodationOfferPageStickyFooter'
import { encodeSearchParams, isRoomsInvalid } from 'lib/search/searchUtils'
import useShareModalConfig from 'hooks/useShareModalConfig'
import WalledContent from 'components/Common/WalledContent'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import AccommodationOfferPageStickyHeaderMobile from 'components/OfferPage/Accommodation/AccommodationOfferPageStickyHeaderMobile'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import OfferPageSignUpWall from 'components/Common/OfferPageSignUpWall'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'
import {
  getCalendarDurationOptionsWithCharterBundleRestrictions,
  getCanUseMultiRoomUI,
  getCheapestSelectedPackageOptionWithDefault,
  getCheckoutDate,
  getEffectiveCharterBundleRoute,
  getEffectiveBundledWithFlightsOnly,
  isAvailableForPurchase,
  offerFlightsEnabled,
} from 'selectors/offerPage/offerPageSelectors'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import GeoContext from 'contexts/geoContext'
import { useLocation } from 'react-router'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import FlashMobileStickyHeaderFilters from './FlashHotelMobileVariant/FlashMobileStickyHeaderFilters'
import { hasSelectedRooms, hasSelectedRoomsOrDefault } from 'lib/offer/offerPageUtil'
import { trackDurationMs } from 'analytics/perf'
import useHotelIndividualRoomAutoselection from 'hooks/OfferPage/useHotelIndividualRoomAutoselection'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import FlashHotelMobileRoomOptions from './FlashHotelMobileVariant/FlashHotelMobileRoomOptions'
import useHasMounted from 'hooks/useHasMounted'
import useCharterFlightLabels from 'hooks/Flights/useCharterFlightLabels'
import type { FareClassWithReward } from 'constants/flight'
import { toBaseFareClass } from 'lib/flights/rewardFareClasses'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import useCharterDateRangesByMonth from 'hooks/CharterFlights/useCharterDateRangesByMonth'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { useCharterBundleFallbackPricing } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useCharterBundleFallbackPricing'
import { filterDateRangesByNights } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/utils'
import FlashHotelDesktopRoomOptions from './FlashHotelDesktopRoomOptions'
import { useIntl } from 'react-intl'
import useMobilePackageSelectCtaExperimentState from '../Accommodation/useMobilePackageSelectCtaExperimentState'
import {
  getFirstAvailableDuration,
  shouldSkipFlashDurationStep,
} from './flashHotelCalendarSkipUtils'
import useExperiment from 'hooks/Optimizely/useExperiment'
import { Experiments } from 'constants/optimizely'

const RelativeBlock = styled.div`
  position: relative;
`

const StyledFlashHotelStickyHeader = styled(FlashHotelHeader)`
  position: relative;

  &.hideOnMobile.hideOnMobile {
    display: none;

    ${mediaQueryUp.tablet} {
      display: flex;
      visibility: visible;
    }
  }
`

const PackageOptionsPane = styled(Pane)`
  ${mediaQueryDown.mobile} {
    background: ${({ theme }) => theme.palette.neutral.surface.darkBackground};

    &.isCharterBundle {
      padding-top: 0;
    }
  }
`

const PackageOptionsContent = styled.div`
  padding: ${rem(20)} 0;
`

interface Props {
  offer: App.HotelOffer
  airport?: App.Airport
  duration: number
  activeFilter?: OfferPageFilterType
  activeHeaderFilter?: OfferPageFilterType
  rooms: Array<OfferPageRoom>
  checkInDate?: string
  checkOutDate?: string
  availableForPurchase?: boolean
  bestPackage: App.OfferPackageOptionView
  room?: App.PackageRoomType
  flightsEnabled?: boolean
  isFlightsSelected: boolean
  roomMode: OfferPageMultiRoomMode
  multipleRoomBookingActive: boolean
  canUseMultiRoomUI: boolean
  charterBundleRoute?: App.CharterBundleRoute
  charterBundleEnabled: boolean
  fareClass: FareClassWithReward
  priceMode: OfferPagePriceMode
  isPriceModeChangedFromDefault: boolean
  effectiveBundledWithFlightsOnly: boolean
  effectiveCharterBundleRoute?: App.CharterBundleRoute
  calendarDurationOptions: Array<OfferDurationOption>
  durationSelected?: boolean
}

/**
 * Until there is a formal way to set a video to auto play on the offer itself
 * We'll see a magic list of offer ids for now
 */
const magicAutoPlayVideoOfferIds = new Set(['006RF00000CoK6HYAV', '006RF00000DX4JtYAL'])

const OFFER_GALLERY_TOP_ROOT_ID = 'offer-gallery-top-root'

function FlashHotelContent(props: Props) {
  const {
    offer,
    airport,
    duration,
    activeFilter,
    rooms,
    checkInDate,
    checkOutDate,
    availableForPurchase,
    bestPackage,
    room,
    activeHeaderFilter,
    flightsEnabled,
    isFlightsSelected,
    roomMode,
    multipleRoomBookingActive,
    canUseMultiRoomUI,
    charterBundleRoute,
    charterBundleEnabled,
    fareClass,
    priceMode,
    isPriceModeChangedFromDefault,
    effectiveBundledWithFlightsOnly,
    effectiveCharterBundleRoute,
    calendarDurationOptions,
    durationSelected,
  } = props

  const intl = useIntl()
  const { charterBundleDurationOptions } = useCharterFlightLabels()
  const location = useLocation()
  const { originAirports, airports } = useContext(GeoContext)
  const luxPlusEnabled = useAppSelector(state => getIsLuxPlusEnabled(state))
  const headlessMode = useAppSelector(state => state.system.headlessMode)
  const prevPath = useAppSelector(state => state.routeHistory.prevPath)
  const isMobileDevice = useScreenSizeOnly('mobile')

  // calendars only start fetching once the user has tried to look at dates
  // after that, try keep the calendars up to date
  const [viewedDates, setViewedDates] = useState(!!checkInDate)

  const { pricing, pricingWithFlights } = bestPackage
  const isHotelOnlyMode = !!charterBundleRoute && !charterBundleEnabled
  const bundledWithFlightsOnly = effectiveBundledWithFlightsOnly
  const shouldShowDiscountPercent = pricing.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = pricingWithFlights && pricingWithFlights.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const showDiscountPill = !offer?.isDiscountPillHidden && (
    (shouldShowDiscountPercent && !bundledWithFlightsOnly) ||
    (bundledWithFlightsOnly && shouldShowBundledDiscountPercent)
  )
  const loggedOutSavedPercent = showDiscountPill
    ? bundledWithFlightsOnly
      ? pricingWithFlights?.discountPercent
      : Math.max(pricing.discountPercent, pricingWithFlights?.discountPercent ?? 0)
    : undefined

  const dispatch = useAppDispatch()
  const stateDispatch = useOfferPageDispatch()
  const packageOptionsAnchor = useRef<HTMLDivElement>(null)
  const topImageLoadSentRef = useRef<boolean>(false)
  const topImageStartMsRef = useRef<number>(0)
  const scrollAfterFilterClose = useRef<boolean>(false)
  const previousRooms = usePrevious(rooms)
  const previousCheckIn = usePrevious(checkInDate)
  const previousFilter = usePrevious(activeFilter)
  const lastFetchedRooms = useRef<Array<App.RoomOccupants> | null>(null)
  const lastFetchedAirport = useRef<App.Airport | null>(null)
  const desktopFilterBlockRef = useRef<HTMLDivElement>(null)
  const [stickyFooterVisible, setIsStickyFooterVisible] = useState<boolean>(true)
  const [multiRoomSelectRoomsFooterInView, setMultiRoomSelectRoomsFooterInView] = useState<boolean>(false)
  const [introMultiRoomSelectRoomsCtaInView, setIntroMultiRoomSelectRoomsCtaInView] = useState<boolean>(true)
  const fareClassForSearch = toBaseFareClass(fareClass)

  const { dateRangesByMonth: charterDateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute,
    occupancies: rooms,
    fareClass: fareClassForSearch,
    disabled: !charterBundleRoute || rooms.length === 0,
  })

  const dateRangesByMonth = useMemo(
    () => filterDateRangesByNights(charterDateRangesByMonth, duration),
    [charterDateRangesByMonth, duration],
  )

  const { totalFlightPrice: flightPrice } = useOfferPageFlightPrice()

  const { cheapestDateRange } = useCharterBundleFallbackPricing({
    offer,
    rooms,
    charterBundleRoute: effectiveCharterBundleRoute,
    fareClass: fareClassForSearch,
    disabled: !effectiveCharterBundleRoute || !!checkInDate,
  })

  const charterBundleCheckoutRange = useMemo(() => {
    if (!charterBundleRoute) return undefined
    const allRanges = Object.values(charterDateRangesByMonth).flatMap((ranges) => ranges)
    if (allRanges.length === 0) return undefined

    const minCheckOut = allRanges.reduce<string>((minValue, range) => {
      if (!minValue) return range.checkOut
      return moment(range.checkOut).isBefore(minValue) ? range.checkOut : minValue
    }, '')
    const maxCheckOut = allRanges.reduce<string>((maxValue, range) => {
      if (!maxValue) return range.checkOut
      return moment(range.checkOut).isAfter(maxValue) ? range.checkOut : maxValue
    }, '')

    return minCheckOut && maxCheckOut ? { minCheckOut, maxCheckOut } : undefined
  }, [charterBundleRoute, charterDateRangesByMonth])

  useEffect(() => {
    if (!charterBundleRoute || !charterBundleCheckoutRange) return
    stateDispatch({
      type: OfferPageActions.setCharterBundleCheckoutRange,
      charterBundleCheckoutRange,
    })
  }, [charterBundleRoute, charterBundleCheckoutRange, stateDispatch])

  const filteredDurationOptions = useMemo(() => {
    return charterBundleDurationOptions.filter(option => {
      return offer.packages.some(pkg => pkg.duration === option.value)
    })
  }, [offer.packages, charterBundleDurationOptions])

  useEffect(() => {
    if (!effectiveCharterBundleRoute) return
    if (filteredDurationOptions.length === 0) return

    const isValidCharterDuration = duration === 5 || duration === 12
    const hasMatchingPackage = filteredDurationOptions.some(opt => opt.value === duration)
    const isValidDuration = isValidCharterDuration && hasMatchingPackage

    if (!isValidDuration) {
      const defaultDuration = filteredDurationOptions[0].value
      if (checkInDate) {
        const newCheckOut = moment(checkInDate).add(defaultDuration, 'days').format(ISO_DATE_FORMAT)
        stateDispatch({ type: OfferPageActions.setDates, checkIn: checkInDate, checkOut: newCheckOut })
      } else {
        stateDispatch({ type: OfferPageActions.setDuration, duration: defaultDuration })
      }
    }
  }, [effectiveCharterBundleRoute, duration, checkInDate, stateDispatch, filteredDurationOptions])

  // Keep duration valid for hotel-only mode (5/6/7 nights only).
  useEffect(() => {
    if (!isHotelOnlyMode) return
    if (calendarDurationOptions.length === 0) return

    const hasValidDuration = calendarDurationOptions.some(option => option.duration === duration)
    if (!hasValidDuration) {
      stateDispatch({ type: OfferPageActions.setDuration, duration: calendarDurationOptions[0].duration })
    }
  }, [duration, isHotelOnlyMode, stateDispatch, calendarDurationOptions])

  const handleCharterDurationChange = useCallback((newNights: number) => {
    // Use setDuration which properly updates duration state and recalculates checkOut if needed
    stateDispatch({ type: OfferPageActions.setDuration, duration: newNights })
  }, [stateDispatch])

  const isWalledGardenEuropeOffersEnabled = useEuropeOfferPageWalledGarden()
  useIdleTimeReporter(IdleTimeSource.FlashHotel)

  const queryString = useMemo(() => {
    return encodeSearchParams({
      urlSearch: location.search,
      dates: {
        checkIn: checkInDate,
        checkOut: checkOutDate,
      },
      rooms,
      multiRoomMode: roomMode,
      fareClass,
      priceMode,
      isPriceModeChangedFromDefault,
      flights: isFlightsSelected,
    }).toString()
  }, [checkInDate, checkOutDate, rooms, roomMode, fareClass, location.search, priceMode, isPriceModeChangedFromDefault, isFlightsSelected])

  // Need to pass the context state above the tree
  useEffect(() => {
    dispatch({
      type: SET_OFFER_CHECK_IN_CHECK_OUT,
      payload: {
        offerId: offer.id,
        checkIn: checkInDate,
        checkOut: checkOutDate,
        duration,
        rooms,
      },
    })

    return
  }, [dispatch, offer.id, checkInDate, duration, rooms, checkOutDate])

  useEffect(() => {
    // clear the check in/check out on unmount
    return () => {
      dispatch({
        type: CLEAR_OFFER_CHECK_IN_CHECK_OUT,
        payload: {
          offerId: offer.id,
        },
      })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    dispatch(fetchRelatedTravelItems(offer, checkInDate, checkOutDate, rooms))
  }, [dispatch, offer, checkInDate, rooms, checkOutDate])

  useMountedEffect(() => {
    scrollAfterFilterClose.current = scrollAfterFilterClose.current ||
      previousRooms !== rooms ||
      previousCheckIn !== checkInDate
  }, [rooms, checkInDate])

  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const scrollToPackageOptions = useCallback(() => {
    requestAnimationFrame(() => {
      packageOptionsAnchor.current?.scrollIntoView({ behavior: 'auto' })
    })
  }, [])

  useEffect(() => {
    topImageStartMsRef.current = performance.now()
    topImageLoadSentRef.current = false
    return
  }, [offer.id])

  // 'auto' scroll (not from a user action) only occurs on tablet and up sizes
  // as we don't want the users scroll to lose their position on mobile
  const autoScrollToFilters = useCallback(() => {
    if (isTabletScreenSizeAndUp) {
      requestAnimationFrame(() => {
        desktopFilterBlockRef.current?.scrollIntoView({ behavior: 'auto' })
      })
    }
  }, [isTabletScreenSizeAndUp])

  const fetchCalendarData = useCallback(() => {
    if (!offer.packages.length || !isValidOccupancy(...rooms)) {
      // if we have any invalid ages or no packages, don't try fetch - it'll fail anyway
      return
    }

    const minDate = offer.bookingMinLeadDays != null
      ? moment().add(offer.bookingMinLeadDays, 'days').format('YYYY-MM-DD')
      : undefined

    dispatch(fetchCalendarsByOccupancy(offer.id, {
      occupants: rooms,
      flightOrigin: airport?.code,
      packages: offer.packages.filter(p => p.duration === duration),
      minDate,
    }))

    if (isMobileDevice || !multipleRoomBookingActive) {
      // 'new mobile hotel offer page' doesn't support per room multi room booking in the modals
      // So we still have to fetch the combined calendar in these circumstances as well
      // the main page will still use the existing multi room if they wish to do it there
      dispatch(fetchCalendars(offer.id, {
        occupants: rooms,
        flightOrigin: airport?.code,
        packages: nonNullable([offer.lowestPricePackage]),
        minDate,
      }))
      dispatch(fetchCalendars(offer.id, {
        occupants: rooms,
        flightOrigin: airport?.code,
        packages: offer.packages.filter(p => p.duration === duration),
        minDate,
      }))
    }
    lastFetchedRooms.current = rooms
    lastFetchedAirport.current = airport ?? null
  }, [airport, multipleRoomBookingActive, dispatch, duration, offer, rooms, isMobileDevice])

  useEffect(() => {
    const useDynamicCancellationPolicies = offer.property.useDynamicCancellationPolicies
    const occupanciesInvalid = rooms.some(item => item.childrenAge?.some(age => age < 0))
    if (checkInDate && checkOutDate && !occupanciesInvalid && !activeFilter && useDynamicCancellationPolicies) {
      if (canUseMultiRoomUI) {
        dispatch(fetchAvailableRatesForEachRoom(
          offer,
          {
            occupants: rooms,
            checkIn: checkInDate,
            checkOut: checkOutDate,
            flightOrigin: airport?.code,
          },
        ))
      } else {
        dispatch(fetchAvailableRatesForOffer(
          offer,
          {
            occupants: rooms,
            checkIn: checkInDate,
            checkOut: checkOutDate,
            flightOrigin: airport?.code,
          },
        ))
      }
    }
  }, [duration, activeFilter, checkInDate, offer, rooms, airport, canUseMultiRoomUI, checkOutDate, dispatch])

  useEffect(() => {
    if (effectiveCharterBundleRoute) {
      fetchCalendarData()
    }
  }, [effectiveCharterBundleRoute, fetchCalendarData])

  useEffect(() => {
    const hasRooms = hasSelectedRooms(rooms)
    const hasDate = !!checkInDate

    if (previousFilter === 'capacity' &&
      !hasRooms &&
      !activeFilter
    ) {
      autoScrollToFilters()
      scrollAfterFilterClose.current = false
    }

    if (activeFilter) {
      autoScrollToFilters()
    }

    if (previousFilter && !activeFilter && hasRooms && hasDate) {
      if (scrollAfterFilterClose.current) {
        if (room) {
          window.location.hash = room.id
        } else {
          autoScrollToFilters()
        }
      }
      scrollAfterFilterClose.current = false
    }

    if ((activeFilter === 'checkin' || activeHeaderFilter === 'checkin') && !viewedDates) {
      setViewedDates(true)
      fetchCalendarData()
    } else {
      const airportChanged = lastFetchedAirport.current !== airport
      const roomsChanged = lastFetchedRooms.current !== rooms
      const requiresNewCalendar = multipleRoomBookingActive ? airportChanged || roomsChanged : viewedDates && (airportChanged || roomsChanged)
      if (requiresNewCalendar) {
        // rooms/airport has changed, all loaded calendars are no longer valid as we load calendars for a specific
        // combinations of flight origin (flight prices) and rooms (availability), so clear them

        // if multiple room booking is available and we changed the rooms,
        // don't clear the previous data, just update for the new changed
        // rooms (is filtered inside fetchCalendarsByOccupancy action)
        if (!multipleRoomBookingActive || isMobileDevice) {
          dispatch(clearBookingCalendar())
        }
        fetchCalendarData()
      }
    }
  }, [activeFilter, activeHeaderFilter, fetchCalendarData]) // oxlint-disable-line react-hooks/exhaustive-deps

  useMountedEffect(() => {
    stateDispatch({ type: OfferPageActions.setOffer, offer })
  }, [offer])

  useEffect(() => {
    // once we load packages and have dates, get our calendar data
    if (viewedDates && offer.packages) {
      fetchCalendarData()
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [offer.packages, multipleRoomBookingActive])

  useMountedEffect(() => {
    // effectively listening on 'on duration change' event
    if (viewedDates) {
      fetchCalendarData()
    }

    if (!activeFilter) {
      autoScrollToFilters()
    }
  }, [duration])

  useMountedEffect(() => {
    if (airport) {
      // effectively an 'on airport change' event
      dispatch(setRecentlyUsedAirportCode(airport.code))
      // Charter bundle offers should not fetch/use non-charter "sample fare" pricing.
      // We still persist the user's selected origin airport to determine feeder eligibility in checkout.
      if (!charterBundleRoute) {
        dispatch(fetchOfferFlightPrice(offer.id, airport.code))
      }
    }
  }, [airport])

  useEffect(() => {
    if (location.hash === urlHashDatePicker && !effectiveCharterBundleRoute) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }
  }, []) // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(replace({ ...location, search: queryString }))
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString])

  const occupancyNumber = useMemo(() => sumUpOccupancies(rooms), [rooms])

  const occupancyLimits = useMemo(() => {
    return offer.packages.flatMap((p) => p.roomRate?.capacities ?? [])
  }, [offer])

  const isValidRoomOccupancy = bundledWithFlightsOnly || occupancyNumber < 10
  const destinationAirport = useMemo(() => airports.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])
  const showFlightInformation = !effectiveCharterBundleRoute && !!flightsEnabled && isFlightsSelected && !!destinationAirport && (originAirports.length > 1 || (originAirports.length > 0 && destinationAirport.code !== airport?.code)) && isValidRoomOccupancy && !!flightPrice

  const hideSoldOutOnMobile = !availableForPurchase && config.LERE_EXPIRED_DEALS_V2_ENABLED

  const shareModalConfig = useShareModalConfig({
    image: offer.images[0],
    offerId: offer.id,
    location: [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ') ?? '',
    type: 'offer',
    utmCampaign: 'webshare',
    queryString,
    offerType: 'hotel',
  })

  // Do not show the Expired recommendation when it comes from my escapes
  const shouldShowLere = useMemo(() => hasOfferExpired(offer) && !prevPath.includes('/my-escapes/order'), [offer, prevPath])

  useHotelIndividualRoomAutoselection()

  const hasMounted = useHasMounted()
  const { isAnyVariant: isFlashCalendarSkipEnabled } = useExperiment(
    Experiments.mobileWebOfferPageFlashCalendarSkip,
    { canActivate: isMobileDevice },
  )
  const defaultOfferDuration = offer.lowestPricePackage?.duration
  const firstAvailableDuration = useMemo(
    () => getFirstAvailableDuration(calendarDurationOptions),
    [calendarDurationOptions],
  )
  const shouldSkipDurationStep = shouldSkipFlashDurationStep({
    isExperimentEnabled: isMobileDevice && isFlashCalendarSkipEnabled,
    durationSelected,
    currentDuration: duration,
    defaultDuration: defaultOfferDuration,
    firstAvailableDuration,
  })
  const isConfirmedMobileFlow = isMobileDevice && hasMounted
  const invalidRoomSelectionForSticky = !hasSelectedRoomsOrDefault(rooms) || isRoomsInvalid(rooms)
  const canViewOptionsForSticky = !!checkInDate && !invalidRoomSelectionForSticky
  const isNewMobileMultiRoomFlow = multipleRoomBookingActive && isConfirmedMobileFlow
  const isNewMobileSingleRoomFlow = !multipleRoomBookingActive
  const canShowMobileMultiRoomSticky =
    isNewMobileMultiRoomFlow &&
    !multiRoomSelectRoomsFooterInView &&
    !introMultiRoomSelectRoomsCtaInView
  const canShowMobileAccommodationSticky =
    isNewMobileSingleRoomFlow ||
    canShowMobileMultiRoomSticky
  const {
    ctaMode: mobileAccommodationStickyFooterCtaMode,
    shouldMountStickyFooter: shouldMountExperimentedMobileStickyFooter,
  } = useMobilePackageSelectCtaExperimentState({
    availableForPurchase,
    canShowCheckAvailabilityCta: canShowMobileAccommodationSticky && !canViewOptionsForSticky,
    canShowPackageSelectCta: canShowMobileAccommodationSticky && canViewOptionsForSticky,
    gateCheckAvailabilityCtaByExperiment: isNewMobileMultiRoomFlow,
    room,
    useMultiRoomSelectRoomsCta: isNewMobileMultiRoomFlow,
  })

  return (
    <ProductPaletteProvider product={offer}>
      <TripGuard>
        <ImmersiveSaveButtonPortal>
          <BookmarkButton offer={offer} />
        </ImmersiveSaveButtonPortal>
      </TripGuard>

      <RelativeBlock onSubmit={(e) => e.preventDefault()}>
        <AccommodationOfferPageStickyHeaderMobile>
          <FlashMobileStickyHeaderFilters
            onCharterDurationChange={handleCharterDurationChange}
          />
        </AccommodationOfferPageStickyHeaderMobile>
        <CSSBreakpoint min="tablet" dynamic>
          <StyledFlashHotelStickyHeader
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
            className={clsx({ hideOnMobile: hideSoldOutOnMobile })}
          >
            {!headlessMode && <DesktopRelatedTravelItems offer={offer} />}
          </StyledFlashHotelStickyHeader>
        </CSSBreakpoint>
        {luxPlusEnabled && !!offer.luxPlus?.access &&
          <LuxPlusNotificationBanner>
            {offer.luxPlus.access === 'earlyAccess' && intl.formatMessage({
              id: 'A5yA5AlpSHEL24rB4ypR',
              defaultMessage: 'Members get early access to this offer before the general public and more',
              description: 'LuxPlus early access notification banner message on the offer detail page',
            })}
            {offer.luxPlus.access === 'memberOnly' && intl.formatMessage({
              id: 'mbgWcWDpuv2RDWPoUuYD',
              defaultMessage: 'You get exclusive access to this member-only offer',
              description: 'LuxPlus member-only notification banner message on the offer detail page',
            })}
          </LuxPlusNotificationBanner>}
        {availableForPurchase && <WalledContent enforced={offer.walledGarden}>
          <Breakpoint only="mobile">
            <FlashHotelSidebarFilters
              capacities={occupancyLimits}
            />
          </Breakpoint>
        </WalledContent>}
        {shouldShowLere && (
          <div>
            <ExpiredDealsLereRecommendations hideOffers={!!activeFilter} />
          </div>
        )}
        <div id={OFFER_GALLERY_TOP_ROOT_ID}>
          <OfferPageGallery
            offer={offer}
            shareModalConfig={shareModalConfig}
            autoPlayVideo={magicAutoPlayVideoOfferIds.has(offer.id) || !!offer.isVideoVimeoHero}
            charterBundleRoute={effectiveCharterBundleRoute}
            onTopHeroImageLoad={() => {
              if (topImageLoadSentRef.current) return
              topImageLoadSentRef.current = true
              trackDurationMs('flash_offer_top_image_load_time_ms', topImageStartMsRef.current)
            }}
          />
        </div>
        <Pane>
          <OfferIntro
            onViewPackageOptions={scrollToPackageOptions}
            flightDestination={destinationAirport}
            shareModalConfig={shareModalConfig}
            onMultiRoomIntroSelectRoomsCtaInViewChange={setIntroMultiRoomSelectRoomsCtaInView}
          />
        </Pane>
        <ScrollAnchor ref={packageOptionsAnchor} />
        <SoftOfferPageWalledGarden>
          <PackageOptionsPane
            id="Package-Options"
            className={clsx({ isCharterBundle: effectiveCharterBundleRoute })}
          >
            <PackageOptionsContent>
              <VerticalSpacer gap={32}>
                <CSSBreakpoint max="mobile" dynamic>
                  <FlashHotelMobileRoomOptions
                    offer={offer}
                    showFlightInformation={showFlightInformation}
                    onSelectRoomsFooterInViewChange={setMultiRoomSelectRoomsFooterInView}
                  />
                </CSSBreakpoint>
                <CSSBreakpoint min="tablet" dynamic>
                  <FlashHotelDesktopRoomOptions
                    offer={offer}
                    setIsStickyFooterVisible={setIsStickyFooterVisible}
                    showFlightInformation={showFlightInformation}
                    charterDateRangesByMonth={dateRangesByMonth}
                    cheapestCharterDateRange={cheapestDateRange}
                    onCharterDurationChange={handleCharterDurationChange}
                    occupancyLimits={occupancyLimits}
                    destinationAirport={destinationAirport}
                    desktopFilterBlockRef={desktopFilterBlockRef}
                  />
                </CSSBreakpoint>
              </VerticalSpacer>
            </PackageOptionsContent>
          </PackageOptionsPane>
          {isMobileDevice && availableForPurchase && shouldMountExperimentedMobileStickyFooter && (
            <AccommodationOfferPageStickyFooter
              ctaMode={mobileAccommodationStickyFooterCtaMode}
              firstAvailableDuration={firstAvailableDuration}
              shouldSkipDurationStep={shouldSkipDurationStep}
            />
          )}
          {hasMounted && !isMobileDevice && availableForPurchase && <HotelOfferPageStickyFooter
            visible={stickyFooterVisible}
            lowestPricePackage={offer.lowestPricePackage ?? offer.packages[0]}
            onScrollTo={scrollToPackageOptions}
            offer={offer}
          />}
        </SoftOfferPageWalledGarden>
      </RelativeBlock>
      <SoftOfferPageWalledGarden bypassWall={isWalledGardenEuropeOffersEnabled} wall={<OfferPageSignUpWall offerType="hotel" saveUpToPercent={loggedOutSavedPercent} />} />
    </ProductPaletteProvider>
  )
}

type MappedProps = Pick<Props,
  | 'airport'
  | 'duration'
  | 'activeFilter'
  | 'rooms'
  | 'checkInDate'
  | 'checkOutDate'
  | 'availableForPurchase'
  | 'bestPackage'
  | 'room'
  | 'activeHeaderFilter'
  | 'flightsEnabled'
  | 'isFlightsSelected'
  | 'roomMode'
  | 'multipleRoomBookingActive'
  | 'canUseMultiRoomUI'
  | 'charterBundleRoute'
  | 'charterBundleEnabled'
  | 'fareClass'
  | 'priceMode'
  | 'isPriceModeChangedFromDefault'
  | 'effectiveBundledWithFlightsOnly'
  | 'effectiveCharterBundleRoute'
  | 'calendarDurationOptions'
  | 'durationSelected'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  airport: state.airport,
  duration: state.duration,
  activeFilter: state.activeFilter,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  checkOutDate: getCheckoutDate(state),
  room: state.room,
  flightsEnabled: offerFlightsEnabled(state, appState),
  isFlightsSelected: state.flights,
  availableForPurchase: isAvailableForPurchase(state, appState),
  bestPackage: getCheapestSelectedPackageOptionWithDefault(appState, state),
  activeHeaderFilter: state.activeHeaderFilter,
  roomMode: state.multiRoomMode,
  multipleRoomBookingActive: isMultipleRoomBookingActive(state, appState),
  canUseMultiRoomUI: getCanUseMultiRoomUI(state, appState),
  charterBundleRoute: state.charterBundleRoute,
  charterBundleEnabled: state.charterBundleEnabled,
  fareClass: state.fareClass,
  priceMode: state.priceMode,
  isPriceModeChangedFromDefault: state.isPriceModeChangedFromDefault,
  effectiveBundledWithFlightsOnly: getEffectiveBundledWithFlightsOnly(state, appState),
  effectiveCharterBundleRoute: getEffectiveCharterBundleRoute(state),
  calendarDurationOptions: getCalendarDurationOptionsWithCharterBundleRestrictions(state, appState),
  durationSelected: state.durationSelected,
}), FlashHotelContent)
